import { Component } from '@angular/core';
import { IonicPage, NavParams, ViewController, ToastController } from 'ionic-angular';
import { AppConfig } from '../../../../../app/app.config';
import {buildMissingFields, MissingFieldGroup, MissingFieldItem} from '../../helpers/jacking-missing-fields.helper';
import {JackingFormConflictResult, JackingFormInstanceEditor} from '../../../../../models/jacking-v2/jacking.models';
import {buildMissingCustom} from '../../helpers/jacking-missing-custom.helper';
import {JackingV2Service} from '../../../../../services/jacking-v2/jacking-v2.service';
import {ValidationResultModel} from '../../../../../_foundation/models/validation.models';

@IonicPage()
@Component({
  selector: 'page-jacking-complete-modal',
  templateUrl: 'jacking-complete-modal.component.html'
})
export class JackingCompleteModalComponent {
  config = AppConfig.settings;

  loading: boolean;

  formInstanceId: number;

  editor: JackingFormInstanceEditor;
  valuesByFieldId: { [fieldTemplateId: number]: any } = {};

  missingGroups: MissingFieldGroup[] = [];
  missingCount = 0;

  constructor(
    private navParams: NavParams,
    private viewCtrl: ViewController,
    private toastCtrl: ToastController,
    private jackingV2Service: JackingV2Service
  ) {}

  ngOnInit() {
    this.formInstanceId = this.navParams.get('jackingFormInstanceId');
    this.editor = this.navParams.get('editor');
    this.valuesByFieldId = this.navParams.get('valuesByFieldId') || this.editor.valuesByFieldId || {};

    if (!this.formInstanceId) {
      this.presentToast('Missing form instance id');
      return;
    }

    const fieldMissing = buildMissingFields({
      template: this.editor.template,
      valuesByFieldId: this.valuesByFieldId
    });

    // custom components don't init until the user opens the section,
    // so we need to build the missing list here instead of in component itself
    const customMissing = buildMissingCustom(this.editor);
    const all = fieldMissing.concat(customMissing);

    this.missingGroups = this.groupMissingFields(all);
    this.missingCount = all.length;
  }

  close() {
    this.viewCtrl.dismiss({ completed: false });
  }

  async confirm() {
    const hasMissing = this.missingCount > 0;

    if (hasMissing && (!this.editor.completionReason || !this.editor.completionReason.trim())) {
      this.presentToast('Please enter a reason for incomplete fields');
      return;
    }

    this.loading = true;
    try {
      await this.jackingV2Service.completeFormInstance(this.editor);
      this.viewCtrl.dismiss({ completed: true });
    } catch (e) {
      if (e?.status === 409) {
        const conflict = e?.error as JackingFormConflictResult;
        this.viewCtrl.dismiss({completed: false, conflict});
        return;
      }
      const validation = e?.error as ValidationResultModel;
      this.viewCtrl.dismiss({ completed: false, validation });
    } finally {
      this.loading = false;
    }
  }

  trackByGroupKey(_: number, g: MissingFieldGroup) {
    return g.key;
  }

  trackByFieldId(_: number, item: MissingFieldItem) {
    return item.fieldTemplateId;
  }

  private groupMissingFields(flat: MissingFieldItem[]): MissingFieldGroup[] {
    const map: { [key: string]: MissingFieldGroup } = {};

    for (const item of flat || []) {
      const sectionName = item.sectionName || 'Other';
      const groupName = item.groupName || '';

      const key = sectionName + '||' + groupName;

      if (!map[key]) {
        map[key] = {
          key,
          sectionName,
          groupName,
          fields: [],
          sectionPageOrder: item.sectionPageOrder,
          groupDisplayOrder: item.groupDisplayOrder
        };
      }

      map[key].fields.push(item);
    }

    // Stable order: by section then group
    const groups = Object.keys(map).map(k => map[k]);
    groups.sort((a, b) => {
      const ao = a.sectionPageOrder != null ? a.sectionPageOrder : 999999;
      const bo = b.sectionPageOrder != null ? b.sectionPageOrder : 999999;
      if (ao !== bo) { return ao - bo; }

      const ag = a.groupDisplayOrder != null ? a.groupDisplayOrder : 999999;
      const bg = b.groupDisplayOrder != null ? b.groupDisplayOrder : 999999;
      if (ag !== bg) { return ag - bg; }

      // fallback stable sort
      const s = (a.sectionName || '').localeCompare(b.sectionName || '');
      if (s !== 0) { return s; }
      return (a.groupName || '').localeCompare(b.groupName || '');
    });


    return groups;
  }

  private presentToast(message: string) {
    this.toastCtrl.create({ message, duration: 2500, position: 'bottom' }).present();
  }

}



// WEBPACK FOOTER //
// ./src/pages/jacking-activities/v2/components/jacking-complete-modal/jacking-complete-modal.component.ts